/**
 * Error text for the agent modals.
 *
 * A mapped BE code renders its catalog copy. Anything else — an unmapped
 * code, a network failure, or a request-schema 400, which bypasses the
 * `{ error: { code } }` envelope and arrives as the raw Zod shape whose
 * `message` is a JSON dump — degrades to one generic localized sentence
 * rather than leaking BE text or rendering nothing.
 */

import { DOMAIN_KEYS, GENERIC_KEYS, localizeKnownError } from '@/api/error-codes'

export const describeAgentError = (err: unknown, t: unknown, fallback: string): string => {
  if (!(err instanceof Error)) return fallback
  return localizeKnownError(err, t) ?? fallback
}

/**
 * A stored error CODE (a run's or a step's `errorCode`) rendered through the
 * same catalog the API errors use. Runs persist the bare code, not an
 * envelope, so this looks the code up directly. An unmapped or missing code
 * degrades to the caller's generic localized sentence rather than showing
 * the raw code.
 */
/** Reasons the agent runtime itself records when a run ends early. */
const RUN_STOP_KEYS: Readonly<Record<string, string>> = {
  run_stale: 'agents:runs.stopReasons.run_stale',
  daily_token_budget: 'agents:runs.stopReasons.daily_token_budget',
  agent_deactivated: 'agents:runs.stopReasons.agent_deactivated',
  turn_error: 'agents:runs.stopReasons.turn_error',
  private_endpoint_requires_premium: 'agents:runs.stopReasons.private_endpoint_requires_premium',
}

export const describeStoredErrorCode = (
  code: string | null | undefined,
  t: unknown,
  fallback: string,
): string => {
  const key = code ? (RUN_STOP_KEYS[code] ?? DOMAIN_KEYS[code] ?? GENERIC_KEYS[code]) : undefined
  return key ? (t as (k: string) => string)(key) : fallback
}
